import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { BadgeCreator } from '../badges/BadgeCreator';
import { Button } from '../ui/Button';
import { BadgeRenderer } from '../badges/BadgeRenderer';

interface EventBadgesPanelProps {
  eventId: number;
}

const EventBadgesPanel: React.FC<EventBadgesPanelProps> = ({ eventId }) => {
  const [defs, setDefs] = useState<any[]>([]);
  const [showCreator, setShowCreator] = useState(false);
  const [vehicleIds, setVehicleIds] = useState('');
  const [selectedDef, setSelectedDef] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const rows = await apiService.listBadgeDefinitions('event', eventId);
    setDefs(rows);
  }, [eventId]);

  useEffect(() => {
    load().catch(() => setDefs([]));
  }, [load]);

  return (
    <section className="rounded-xl border border-white/[0.06] p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg text-white">Badges and recognition</h2>
          <p className="text-sm text-gray-500">Create controlled event credentials. Issued designs are snapshotted.</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreator((v) => !v)}>
          {showCreator ? 'Close' : 'Create badge'}
        </Button>
      </div>

      {showCreator ? (
        <BadgeCreator
          sourceType="event"
          sourceId={eventId}
          onSaved={async () => {
            setShowCreator(false);
            await load();
          }}
        />
      ) : null}

      <ul className="space-y-2">
        {defs.map((d) => (
          <li key={d.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] p-3">
            <BadgeRenderer design={d} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{d.name}</p>
              <p className="text-xs text-gray-500">
                {d.status} · {d.badge_category} · {d.issuance_method}
              </p>
            </div>
            {d.status === 'draft' ? (
              <Button
                variant="secondary"
                onClick={async () => {
                  await apiService.activateBadgeDefinition(d.id, 'activate');
                  await load();
                }}
              >
                Activate
              </Button>
            ) : null}
            {d.status === 'active' ? (
              <Button variant="ghost" onClick={() => setSelectedDef(d.id)}>
                Issue
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {selectedDef ? (
        <div className="rounded-lg border border-white/[0.06] p-3 space-y-2">
          <p className="text-sm text-gray-300">Bulk issue to registered vehicle IDs (comma-separated)</p>
          <input
            className="input"
            value={vehicleIds}
            onChange={(e) => setVehicleIds(e.target.value)}
            placeholder="12, 34, 56"
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={async () => {
                const ids = vehicleIds
                  .split(',')
                  .map((x) => Number(x.trim()))
                  .filter(Boolean);
                const res = await apiService.bulkIssueBadges(selectedDef, ids);
                setMessage(`Issued batch complete (${(res as any).data?.length || 0} results)`);
                setSelectedDef(null);
                setVehicleIds('');
              }}
            >
              Issue
            </Button>
            <Button variant="ghost" onClick={() => setSelectedDef(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {message ? <p className="text-sm text-emerald-400">{message}</p> : null}
    </section>
  );
};

export default EventBadgesPanel;
